// check-enabled-tools-health.ts
//
// Doctor check: với mỗi tool enabled=true trong state, xác minh
//   (a) manifest còn trong pack (phát hiện version skew / orphan state)
//   (b) hook của tool THỰC SỰ có trong settings.json (phát hiện "state nói bật
//       nhưng settings bị ghi đè/mất hook" — fixable bằng re-enable)
//
// Trả về list check shape khớp doctor CheckResult (name/status/detail/fixable/fix).

import { join } from "node:path";
import {
  enableTool,
  readToolManifest,
  type ToolManifest,
} from "./apply-tool-manifest-to-settings.js";
import { checkPlanMonitorTelemetryHealth } from "./check-plan-monitor-telemetry-health.js";
import { pathExists, readJson } from "./filesystem-helpers.js";
import { commandsInEntries } from "./hook-command-list-from-entries.js";
import { listEnabledTools } from "./tool-state-store.js";

export interface ToolCheck {
  name: string;
  status: "ok" | "warn" | "fail";
  detail: string;
  fixable: boolean;
  fix?: () => Promise<void>;
}

// Exported để check-plan-monitor-telemetry-health.ts dùng chung shape (đọc
// settings.json — không nhân bản type). Type-only import ở phía kia nên
// isolatedModules erase hoàn toàn, không tạo runtime circular import.
export interface SettingsShape {
  hooks?: Record<string, Array<{ matcher?: string; hooks?: Array<{ command?: string }> }>>;
}

// plan-monitor là vỏ (manifest.settings.hooks={}) — generic manifestHooksPresent()
// bên dưới luôn true trivially cho vỏ rỗng (GAP E22). Route qua check riêng bám
// telemetry (nơi collector thật sự sống) thay vì dùng generic check.
const PLAN_MONITOR_TOOL_NAME = "plan-monitor";

// Mọi command của tool đã có mặt trong settings.json chưa?
// commandsInEntries() guard shape (settings.json parse OK nhưng sai dạng, vd
// hooks.PreToolUse: "abc" thay vì array) — trước fix, `sHooks[event] ?? []`
// KHÔNG bắt được case này (giá trị truthy không phải array vẫn qua ??) →
// present.flatMap ném TypeError, sập checkEnabledToolsHealth kéo MỌI tool khác
// trong batch báo fail theo dù không liên quan.
function manifestHooksPresent(settings: SettingsShape, manifest: ToolManifest): boolean {
  const mHooks = manifest.settings.hooks ?? {};
  for (const [event, featEntries] of Object.entries(mHooks)) {
    const presentCommands = new Set(commandsInEntries(settings.hooks?.[event]));
    for (const entry of featEntries) {
      for (const h of entry.hooks ?? []) {
        if (h.command && !presentCommands.has(h.command)) {
          return false;
        }
      }
    }
  }
  return true;
}

export async function checkEnabledToolsHealth(cwd: string): Promise<ToolCheck[]> {
  const enabled = await listEnabledTools(cwd);
  if (enabled.length === 0) {
    return [];
  }

  const settingsPath = join(cwd, ".claude", "settings.json");
  let settings: SettingsShape = {};
  if (await pathExists(settingsPath)) {
    try {
      settings = await readJson<SettingsShape>(settingsPath);
    } catch {
      settings = {};
    }
  }

  const checks: ToolCheck[] = [];
  for (const name of enabled) {
    const manifest = await readToolManifest(cwd, name);
    if (!manifest) {
      checks.push({
        name: `Tool: ${name}`,
        status: "warn",
        detail: "state=enabled nhưng pack thiếu manifest (version skew). Chạy 'avatar sync'.",
        fixable: false,
      });
      continue;
    }
    if (name === PLAN_MONITOR_TOOL_NAME) {
      checks.push(await checkPlanMonitorTelemetryHealth(cwd, settings));
      continue;
    }

    if (manifestHooksPresent(settings, manifest)) {
      checks.push({
        name: `Tool: ${name}`,
        status: "ok",
        detail: `enabled, hook active (v${manifest.version})`,
        fixable: false,
      });
    } else {
      checks.push({
        name: `Tool: ${name}`,
        status: "fail",
        detail: "state=enabled nhưng hook thiếu trong settings.json — fixable (re-apply)",
        fixable: true,
        fix: async () => {
          await enableTool(cwd, manifest);
        },
      });
    }
  }
  return checks;
}
